import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import PaymentsIcon from "@mui/icons-material/Payments";
import DashboardIcon from "@mui/icons-material/Dashboard";
import WifiIcon from "@mui/icons-material/Wifi";
import BarChartIcon from "@mui/icons-material/BarChart";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Link as RouterLink, Outlet, useLocation, matchPath } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BusyButton } from "../../Platform/XprobaseConsole/shared";
import { PARTNER_CONSOLE_BASE, PARTNER_CONSOLE_PATH } from "./partnerConsoleRoutes";
import { usePartnerBilling } from "./PartnerBillingContext";
import { usePartnerTerminology } from "./hooks/usePartnerTerminology";
import { useQueryClient } from "react-query";
import { clearActiveWorkspaceBrowserStorage } from "../../../utils/workspaceLocalStorage";
import { useBrand } from "../../../context/BrandContext";
import { PLATFORM_APP_VERSION } from "../../../appVersion";

const DRAWER_WIDTH = 272;

const isPartnerTeamPath = (pathname) =>
  pathname === PARTNER_CONSOLE_PATH.team || pathname.startsWith(`${PARTNER_CONSOLE_PATH.team}/`);

const NAV_ITEMS = [
  { path: PARTNER_CONSOLE_PATH.overview, match: PARTNER_CONSOLE_PATH.overview, Icon: DashboardIcon, labelKey: "platformPage.partnerNavOverview", capability: null },
  { path: `${PARTNER_CONSOLE_PATH.dashboard}/live`, match: PARTNER_CONSOLE_PATH.dashboard, Icon: BarChartIcon, labelKey: "platformPage.partnerNavDashboard", capability: "viewLiveDashboard" },
  { path: PARTNER_CONSOLE_PATH.passagens, match: PARTNER_CONSOLE_PATH.passagens, Icon: ConfirmationNumberOutlinedIcon, labelKey: "platformPage.partnerNavPassagens", capability: "viewPassengerSales" },
  { path: PARTNER_CONSOLE_PATH.workspaces, match: PARTNER_CONSOLE_PATH.workspaces, Icon: WorkOutlineIcon, labelKey: "workspacesNav", capability: null },
  { path: PARTNER_CONSOLE_PATH.presence, match: PARTNER_CONSOLE_PATH.presence, Icon: WifiIcon, labelKey: "platformPage.partnerNavPresence", capability: "viewPresence" },
  { path: PARTNER_CONSOLE_PATH.pricing, match: PARTNER_CONSOLE_PATH.pricing, Icon: PaymentsIcon, labelKey: "platformPage.partnerNavPricing", capability: "viewPlansAndBilling" },
  { path: PARTNER_CONSOLE_PATH.team, match: PARTNER_CONSOLE_PATH.team, Icon: ForumOutlinedIcon, labelKey: "platformPage.navTeam", capability: "viewTeam" },
];

function pathMatchesNav(pathname, navMatch) {
  if (pathname === navMatch) return true;
  if (pathname.startsWith(`${navMatch}/`)) return true;
  return false;
}

const PartnerConsoleShell = () => {
  const { t } = useTranslation("app");
  const theme = useTheme();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Ao (re)entrar no portal: zera tenant storage + cache em memória para não misturar workspaces.
  useEffect(() => {
    clearActiveWorkspaceBrowserStorage();
    queryClient.clear();
  }, [queryClient]);
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [globalError, setGlobalError] = useState("");
  const [actionToast, setActionToast] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const { reseller, capabilities, profile } = usePartnerBilling();
  const { badge, navWorkspaces, consoleSubtitle } = usePartnerTerminology();
  const brand = useBrand();

  const caps = capabilities || profile?.capabilities || {};

  const visibleNavItems = useMemo(
    () =>
      NAV_ITEMS.filter((item) => {
        if (!item.capability) return true;
        return caps[item.capability] !== false;
      }),
    [caps]
  );

  const refreshRef = useRef(() => {});

  const stopRefreshing = useCallback(() => setRefreshing(false), []);

  const registerRefresh = useCallback((fn) => {
    refreshRef.current = typeof fn === "function" ? fn : () => {};
  }, []);

  const runRefresh = useCallback(() => {
    setRefreshing(true);
    try {
      refreshRef.current();
    } catch {
      stopRefreshing();
    }
  }, [stopRefreshing]);

  const showActionToast = useCallback(
    (msg) => {
      if (msg) setActionToast(msg);
      stopRefreshing();
    },
    [stopRefreshing]
  );

  const outletContext = useMemo(
    () => ({
      registerRefresh,
      stopRefreshing,
      setGlobalError,
      showActionToast,
    }),
    [registerRefresh, stopRefreshing]
  );

  const onWorkspaceDetail = Boolean(matchPath(`${PARTNER_CONSOLE_BASE}/workspaces/:dominioId`, location.pathname));
  const onWorkspaceCreate = Boolean(
    matchPath({ path: PARTNER_CONSOLE_PATH.workspaceCreate, end: true }, location.pathname)
  );
  const isTeamRoute = isPartnerTeamPath(location.pathname);

  const partnerSubtitle = consoleSubtitle;

  const navLabel = useCallback(
    (item) => {
      if (item.labelKey === "workspacesNav") return navWorkspaces;
      return t(item.labelKey);
    },
    [navWorkspaces, t]
  );

  const drawer = (
    <Box sx={{ pt: 1.25, pb: 2.5, px: 0.25 }} component="nav" aria-label={t("platformPage.partnerNavAriaLabel")}>
      <Typography variant="overline" sx={{ px: 2.25, color: "text.secondary", letterSpacing: 0.6 }}>
        {t("partnerConsole.shell.navSection", { defaultValue: "Menu" })}
      </Typography>
      <List dense sx={{ mt: 0.5 }}>
        {visibleNavItems.map(({ path, match, Icon, labelKey }) => {
          const selected =
            pathMatchesNav(location.pathname, match) ||
            (labelKey === "workspacesNav" && onWorkspaceDetail);
          return (
            <ListItemButton
              key={path}
              component={RouterLink}
              to={path}
              selected={selected}
              onClick={() => setMobileOpen(false)}
              sx={{
                mx: 1,
                my: 0.125,
                py: 1,
                borderRadius: 1.5,
                "&.Mui-selected": {
                  bgcolor: (th) => alpha(th.palette.primary.main, 0.12),
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Icon fontSize="small" color={selected ? "primary" : "inherit"} />
              </ListItemIcon>
              <ListItemText
                primary={navLabel({ labelKey })}
                primaryTypographyProps={{ fontWeight: selected ? 600 : 500, fontSize: "0.9375rem" }}
              />
            </ListItemButton>
          );
        })}
      </List>
      <Box sx={{ padding: "16px", textAlign: "left", fontSize: "12px", color: theme.palette.text.disabled }}>
        © {new Date().getFullYear()} {brand.name} · v{PLATFORM_APP_VERSION}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        pt: isTeamRoute ? { xs: 1, md: 1.5 } : { xs: 2, sm: 2.5, md: 3 },
        pb: isTeamRoute ? 0 : { xs: 5, md: 6 },
        px: isTeamRoute ? { xs: 1, sm: 1.5, md: 2 } : { xs: 2, sm: 3, md: 4 },
        maxWidth: isTeamRoute ? "none" : 1680,
        mx: isTeamRoute ? 0 : "auto",
        width: 1,
        minWidth: 0,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        overflow: isTeamRoute ? "hidden" : "visible",
      }}
    >
      <a href="#partner-console-main" style={{ position: "absolute", left: -9999 }}>
        {t("platformPage.partnerSkipToContent")}
      </a>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={{ xs: 2, sm: 2.5 }}
        sx={{ mb: { xs: 2.5, md: 3.5 } }}
      >
        <Box sx={{ minWidth: 0, display: "flex", alignItems: "flex-start", gap: 1 }}>
          {!isMdUp ? (
            <IconButton aria-label={t("platformPage.openMenu")} onClick={() => setMobileOpen(true)} edge="start" size="small">
              <MenuIcon />
            </IconButton>
          ) : null}
          <Box sx={{ pt: { xs: 0.25, sm: 0 } }}>
            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
              <Typography variant="h4" component="h1" sx={{ letterSpacing: -0.02 }}>
                {t("platformPage.partnerConsoleTitle")}
              </Typography>
              {badge ? (
                <Chip size="small" label={badge} color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
              ) : null}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 720, lineHeight: 1.55 }}>
              {partnerSubtitle}
            </Typography>
          </Box>
        </Box>
        {onWorkspaceCreate ? null : (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Tooltip title={t("platformPage.partnerRefreshSectionTooltip")}>
              <span>
                <BusyButton
                  startIcon={<RefreshIcon />}
                  variant="outlined"
                  size="small"
                  onClick={runRefresh}
                  loading={refreshing}
                  loadingText={t("partnerConsole.shell.refreshing", { defaultValue: "Atualizando…" })}
                >
                  {t("partnerConsole.shell.refreshSection", { defaultValue: "Atualizar página" })}
                </BusyButton>
              </span>
            </Tooltip>
          </Stack>
        )}
      </Stack>

      {globalError ? (
        <Alert severity="error" sx={{ mb: { xs: 2.5, md: 3 } }} onClose={() => setGlobalError("")}>
          {globalError}
        </Alert>
      ) : null}

      <Snackbar
        open={Boolean(actionToast)}
        autoHideDuration={6000}
        onClose={() => setActionToast("")}
        message={actionToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />

      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          gap: { xs: 0, md: isTeamRoute ? 2 : 3 },
          minWidth: 0,
          width: 1,
          flex: isTeamRoute ? 1 : undefined,
          minHeight: isTeamRoute ? 0 : undefined,
          overflow: isTeamRoute ? "hidden" : "visible",
        }}
      >
        {isMdUp ? (
          <Paper
            variant="outlined"
            sx={{
              width: DRAWER_WIDTH,
              flexShrink: 0,
              borderRadius: 2,
              alignSelf: "flex-start",
              position: "sticky",
              top: { md: 20 },
              maxHeight: { md: "calc(100vh - 40px)" },
              overflow: "auto",
              py: 0.5,
              boxShadow: (th) => `0 1px 0 ${alpha(th.palette.divider, 0.9)}`,
            }}
          >
            {drawer}
          </Paper>
        ) : (
          <Drawer anchor="left" open={mobileOpen} onClose={() => setMobileOpen(false)} PaperProps={{ sx: { width: DRAWER_WIDTH } }}>
            {drawer}
            <Divider />
            <Box sx={{ p: 2 }}>
              <Button fullWidth variant="outlined" onClick={() => setMobileOpen(false)}>
                {t("platformPage.closeMenu")}
              </Button>
            </Box>
          </Drawer>
        )}

        <Box
          id="partner-console-main"
          tabIndex={-1}
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: isTeamRoute ? 0 : undefined,
            pt: { xs: 0.5, md: 0 },
            pb: isTeamRoute ? 0 : { xs: 1, md: 0 },
            display: isTeamRoute ? "flex" : "block",
            flexDirection: isTeamRoute ? "column" : undefined,
            overflow: isTeamRoute ? "hidden" : "visible",
          }}
        >
          <Outlet context={outletContext} />
        </Box>
      </Box>
    </Box>
  );
};

export default PartnerConsoleShell;
